import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { noticesStore, userStore } from 'stores/global';

import {
  type AssetLibraryClickHandlers,
  applyAssetLibraryClickAction,
  resolveAssetLibraryClickAction,
} from '../assetLibraryActions';
import { getBookmarkReferenceAssets } from '../assetLibraryAssetsLoad';
import { useAssetLibraryEmbedConfig } from '../assetLibraryEmbedContext';
import type { MotionLibraryDisplayItem } from '../assetLibraryMotionLibrary';
import { resolveMotionJobIdFromSceneAsset } from '../assetLibrarySceneMotion';
import { useStore } from '../assetLibraryStore';
import { useAssetLibraryMediaSelection } from '../hooks/useAssetLibraryMediaSelection';
import type { AssetLibraryDisplayItem } from '../utils';
import { getAssetLibraryItemKey, isMainLibraryPageItem } from '../utils';

export function useAssetLibraryPickHandlers() {
  const store = useStore();
  const navigate = useNavigate();
  const embedConfig = useAssetLibraryEmbedConfig();
  const mediaSelection = useAssetLibraryMediaSelection();
  const { libraryView } = store.state;
  const bulkSelectionEnabled = !embedConfig && libraryView === 'all';

  const handlePickMogenJob = useCallback(
    (input: { jobId: string; label: string; outputCount?: number }) => {
      embedConfig?.onPick?.({ kind: 'mogen-job', ...input });
    },
    [embedConfig],
  );

  const handlePickStockMotion = useCallback(
    (input: { motionId: string; label: string }) => {
      embedConfig?.onPick?.({ kind: 'stock-motion', ...input });
    },
    [embedConfig],
  );

  const handlePickSceneMotion = useCallback(
    async ({ sceneId, label }: { sceneId: string; label: string }) => {
      const apiKey = userStore.state.apiKey.data?.apiKey;
      if (!apiKey) {
        noticesStore.error('Failed to authenticate. Please try again.');
        return;
      }

      const resolved = await resolveMotionJobIdFromSceneAsset(apiKey, sceneId);
      if (!resolved) {
        noticesStore.error('Unable to load motion from scene.');
        return;
      }

      handlePickMogenJob({
        jobId: resolved.jobId,
        label: resolved.label ?? label,
      });
    },
    [handlePickMogenJob],
  );

  const handleOpenScene = useCallback(
    ({ sceneId }: { sceneId: string }) => {
      embedConfig?.onOpenScene?.(sceneId);
    },
    [embedConfig],
  );

  const clickHandlers = useMemo<AssetLibraryClickHandlers>(
    () => ({
      navigate,
      openGroupDetail: (target) => store.openGroupDetail(target),
      openMediaPreview: (preview) => store.openMediaPreview(preview),
      openJobPreview: (input) => store.openJobPreview(input),
      selectMogenJob: handlePickMogenJob,
      selectSceneMotion: ({ sceneId, label }) => {
        void handlePickSceneMotion({ sceneId, label });
      },
      openScene: handleOpenScene,
      selectStockMotion: handlePickStockMotion,
      toggleMediaSelection: (asset) => mediaSelection?.toggleAsset(asset),
    }),
    [
      handleOpenScene,
      handlePickMogenJob,
      handlePickSceneMotion,
      handlePickStockMotion,
      mediaSelection,
      navigate,
      store,
    ],
  );

  const getAssetClickHandler = useCallback(
    (asset: AssetLibraryDisplayItem) => {
      const showMainLibraryPreviewActions = isMainLibraryPageItem(
        Boolean(embedConfig),
        libraryView,
      );
      const showOpenWithActions = !embedConfig && libraryView !== 'trash';
      const action = resolveAssetLibraryClickAction(asset, {
        embedded: Boolean(embedConfig),
        libraryView,
        pickMode: embedConfig?.pickMode,
        openSceneOnClick: embedConfig?.openSceneOnClick,
        stagedMediaSelection: mediaSelection?.isStagedSelection,
        showMainLibraryPreviewActions,
        showOpenWithActions,
        bookmarkReferenceAssets: getBookmarkReferenceAssets(store.state),
      });
      if (!action) {
        return undefined;
      }

      return () => applyAssetLibraryClickAction(action, clickHandlers);
    },
    [
      clickHandlers,
      embedConfig,
      libraryView,
      mediaSelection?.isStagedSelection,
      store,
    ],
  );

  const onToggleAssetSelection = useCallback(
    (asset: AssetLibraryDisplayItem) => {
      store.toggleAssetSelection(asset);
    },
    [store],
  );

  const handleStockMotionLibraryClick = useCallback(
    (motion: MotionLibraryDisplayItem) => {
      if (embedConfig?.pickMode) {
        handlePickStockMotion({
          motionId: motion.motionID,
          label: motion.title,
        });
        return;
      }

      store.openStockMotionPreview(motion);
    },
    [embedConfig?.pickMode, handlePickStockMotion, store],
  );

  const isAssetSelected = useCallback(
    (asset: AssetLibraryDisplayItem) => {
      if (bulkSelectionEnabled) {
        return store.isAssetSelectionKeySelected(getAssetLibraryItemKey(asset));
      }

      return mediaSelection?.isSelected(asset.assetID) ?? false;
    },
    [bulkSelectionEnabled, mediaSelection, store],
  );

  return {
    pickMode: embedConfig?.pickMode ?? false,
    bulkSelectionEnabled,
    handlePickMogenJob,
    getAssetClickHandler,
    handleStockMotionLibraryClick,
    isAssetSelected,
    onToggleAssetSelection,
  };
}
